<template>
  <b-container fluid class="theme-yellow">
    <div class="page-header">
      <Navbar text="Companion" />
    </div>

    <section>
      <div class="row">
        <div class="col-sm-6">
          <h2 class="card-title">
            Immersive Brochures
            <StorageStatus
              :stored="brochuresCount"
              :limit="brochuresLimit"
            />
          </h2>
        </div>
        <div class="col-sm-6 text-right">
          <b-button
            variant="primary"
            v-b-modal="ADD_BROCHURE_MODAL"
            :disabled="!canCreateBrochure"
          >
            Create
          </b-button>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-7 col-lg-4 text-center">
          <div class="form-inline mb-3">
            <div class="form-group">
              <input
                type="search"
                name="filter"
                class="form-control"
                placeholder="Search..."
                v-model="filter"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="table-responsive">
        <b-table
          class="dataTable brochures-table"
          striped
          :fields="fields"
          :items="brochures"
          :busy="isBusy"
          :filter="filter"
        >
          <div slot="table-busy" class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>

          <template #cell(accessCode)="data">
            <a
              href="javascript: void(0);"
              @click.prevent="toggleQrCodeModal(data.item)"
            >
              {{ data.item.accessCode }}
            </a>
          </template>

          <template #cell(models)="data">
            <ul>
              <li v-for="model in data.item.models" :key="model.id">
                <span>{{ model.modelName }}</span>
                <span v-if="!stringUtils.isNullOrWhiteSpace(model.modelAlias)">
                  ({{ model.modelAlias }})</span
                >
              </li>
            </ul>
          </template>
          
          <template #cell(actions)="data">
            <a
              href="javascript: void(0);"
              class="d-block d-md-inline"
              @click="onRemoveBrochureButtonClicked(data.item.id)"
            >
              <span class="icon icon-trash" />
            </a>
          </template>
        </b-table>
      </div>
    </section>

    <!-- Modals -->
    <AddBrochureModal
      :modal-id="ADD_BROCHURE_MODAL"
      v-on:brochure-posted="onBrochurePosted"
      v-on:brochure-post-failed="onBrochurePostFailed"
    />

    <QRCodeModal 
      :modal-id="QR_CODE_MODAL"
      :custom-deep-link-url="customDeepLinkUrl"
    />
  </b-container>
</template>

<script>
import Navbar from "../Navigation/Navbar";
import StorageStatus from "@/components/Views/StorageStatus";
import AddBrochureModal from "@/components/Views/AddBrochureModal";
import QRCodeModal from "@/components/Views/QRCodeModal";
import * as harpraApi from "@/services/api.service";
import * as routerUtils from "@/utils/router-utils.js";
import * as stringUtils from "@/utils/string-utils";
import * as formatUtils from "@/utils/format-utils";
import events from '@/enums/event-types'
import modals from '@/enums/modal-types'

export default {
  name: "Brochures",
  data() {
    return {
      isSubscriptionActive: false,
      brochures: [],
      fields: [
        { key: "title", label: "Title", sortable: true },
        { key: "description", label: "Description" },
        { key: "accessCode", label: "Access Code" },
        { key: "models", label: "Models" },
        {
          key: "creationDate",
          label: "Date Created",
          formatter: "formatDate",
          sortable: true,
        },
        { key: "actions", label: "Actions" },
      ],
      isBusy: true,
      filter: "",
      brochuresCount: null,
      brochuresLimit: null,
      customDeepLinkUrl: "",
      canCreateBrochure: false,
      displayedAccessCode: null,
      QR_CODE_MODAL: modals.qrCodeModal,
      ADD_BROCHURE_MODAL: modals.addBrochureModal,
    };
  },
  components: {
    AddBrochureModal,
    QRCodeModal,
    Navbar,
    StorageStatus,
  },
  computed: {
    stringUtils() {
      return stringUtils;
    },
  },
  methods: {
    formatDate: formatUtils.formatDate,
    async loadBrochures(accessToken) {
      this.isBusy = true;
      try {
        this.brochures = await harpraApi.getBrochures(accessToken);
        this.brochuresCount = this.brochures.length;
      } finally {
        this.isBusy = false;
        this.canCreateBrochure = this.brochuresCount < this.brochuresLimit;
      }
    },
    async removeBrochure(accessToken, brochureId) {
      await harpraApi.deleteBrochure(accessToken, brochureId);
    },
    onBrochurePosted() {
      this.$AuthService.getAccessToken(async () => {
        let accessToken = this.$AuthService.token.accessToken;

        await this.loadBrochures(accessToken);
      });
    },
    onBrochurePostFailed() {
      this.$AuthService.getAccessToken(async () => {
        let accessToken = this.$AuthService.token.accessToken;

        await this.loadBrochures(accessToken);
      });
    },
    onRemoveBrochureButtonClicked(brochureId) {
      this.$bvModal
        .msgBoxConfirm("Are you sure you want to remove this brochure?", {
          title: "Remove Brochure",
          size: "sm",
          okVariant: "danger",
          okTitle: "YES",
          cancelVariant: "black",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
          modalClass: "regular",
        })
        .then(async (confirmed) => {
          if (!confirmed) {
            return;
          }

          this.$AuthService.getAccessToken(async () => {
            let accessToken = this.$AuthService.token.accessToken;

            await this.removeBrochure(accessToken, brochureId);
            await this.loadBrochures(accessToken);
          });
        });
    },
    toggleQrCodeModal(brochure) {
      this.$eventBus.$emit(events.toggleQrCodeModal, brochure);
    },
  },
  created() {
    this.$AuthService.getAccessToken(
      async () => {
        let accessToken = this.$AuthService.token.accessToken;

        try {
          let subscriptionInfo = await harpraApi.getSubscriptionInfo(
            accessToken
          );

          this.isSubscriptionActive = subscriptionInfo.isActive;
          this.isCompanionActive = subscriptionInfo.isCompanionActive;
          this.brochuresLimit = subscriptionInfo.brochuresLimit;
          this.customDeepLinkUrl = subscriptionInfo.customDeepLinkUrl;
        } catch (error) {
          console.log("[WARN] Couldn't get subscription info.");

          this.isSubscriptionActive = false;
        }

        if (this.isSubscriptionActive && this.isCompanionActive) {
          await this.loadBrochures(accessToken);

          if (this.$route.query["add"]) {
            routerUtils.removeQueryParam(this, "add");
            this.$bvModal.show(modals.addBrochureModal);
          }
        } else {
          routerUtils.routeToAccount(this);
        }
      },
      () => {
        console.log("[ERROR] Couldn't get access token.");
      }
    );
  },
  mounted() {},
};
</script>
